/**
 * MonitorTypeBadge - Visual indicator for monitor trigger type
 *
 * Displays a color-coded badge representing the monitor type.
 * Color scheme:
 * - scheduled: Blue - Runs on a fixed schedule
 * - continuous: Purple - Continuously monitors
 * - event-driven: Orange - Triggered by filesystem events
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import type { MonitorType } from '@/features/monitors/types/monitor.types';

interface MonitorTypeBadgeProps {
  /**
   * Monitor trigger type to display
   */
  type: MonitorType;
  /**
   * Optional additional CSS classes
   */
  className?: string;
}

/**
 * Get custom color classes for monitor type badge
 */
const getColorClasses = (type: MonitorType): string => {
  switch (type) {
    case 'scheduled':
      return 'bg-info/10 text-info border-info/30';
    case 'continuous':
      return 'bg-violet/10 text-violet border-violet/30';
    case 'event-driven':
      return 'bg-alert/10 text-alert border-alert/30';
    default:
      return '';
  }
};

/**
 * Get descriptive tooltip text for monitor type
 */
const getTooltipText = (type: MonitorType): string => {
  switch (type) {
    case 'scheduled':
      return 'Monitor runs on a fixed schedule (daily, hourly, etc.)';
    case 'continuous':
      return 'Monitor continuously polls the directory for changes';
    case 'event-driven':
      return 'Monitor responds to filesystem events in real-time';
    default:
      return '';
  }
};

/**
 * Get display label for type
 */
const getTypeLabel = (type: MonitorType): string => {
  switch (type) {
    case 'scheduled':
      return 'Scheduled';
    case 'continuous':
      return 'Continuous';
    case 'event-driven':
      return 'Event-Driven';
    default:
      return type;
  }
};

/**
 * MonitorTypeBadge Component
 *
 * Renders a styled badge indicating the monitor trigger type.
 *
 * @example
 * ```tsx
 * <MonitorTypeBadge type="scheduled" />
 * <MonitorTypeBadge type="continuous" className="ml-2" />
 * ```
 */
export const MonitorTypeBadge: React.FC<MonitorTypeBadgeProps> = ({ type, className }) => {
  const colorClasses = getColorClasses(type);
  const tooltipText = getTooltipText(type);
  const label = getTypeLabel(type);

  return (
    <Badge
      variant="outline"
      className={`${colorClasses} border font-semibold ${className || ''}`}
      title={tooltipText}
    >
      {label}
    </Badge>
  );
};
